import { Chip, DataTable, HairlineCard, Stack, TagChip, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useScope } from '../../providers/ScopeProvider';

/**
 * The account's own standing: where it can work, in what capacity, and what
 * that lets it do here.
 *
 * Read-only and available to everyone, because "why can't I see that file?" is
 * the most common support question a scoped system creates, and the answer is
 * almost always on this screen.
 */
export function MyAccessPanel() {
  const { t } = useTranslation();
  const { scopes, active, can } = useScope();

  // The handful of powers worth stating plainly. A full permission dump would
  // be honest and unreadable; these are the ones people ask about.
  const notable = [
    'contract:sign',
    'officer:appoint',
    'officer:propose',
    'member:appoint',
    'employee:manage',
    'file:team:manage',
    'signing:grant',
    'file:advance',
    'file:read',
  ] as const;

  // An account attached to nowhere — a retired seat, or a hire nobody has
  // appointed yet. The table would be headers over nothing and the summary
  // below would read "At  you can", so this state gets its own answer: what is
  // missing, and who can grant it.
  if (scopes.length === 0) {
    return (
      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {t('gov.admin.me.noScopes')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.me.noScopesBody')}
        </Typography>
      </HairlineCard>
    );
  }

  return (
    <Stack spacing={2}>
      <DataTable
        rows={scopes}
        getRowKey={(scope) => `${scope.kind}:${scope.id}`}
        emptyLabel={t('gov.admin.me.noScopes')}
        initialSortKey="place"
        rowSx={(scope) =>
          scope.id === active?.id && scope.kind === active?.kind
            ? { backgroundColor: 'action.selected' }
            : undefined
        }
        columns={[
          {
            key: 'place',
            label: t('gov.admin.me.place'),
            primary: true,
            sortValue: (scope) => scope.name,
            cell: (scope) => scope.name,
          },
          {
            key: 'kind',
            label: t('gov.admin.me.kind'),
            sortValue: (scope) => scope.kind,
            cell: (scope) => t(`gov.admin.me.kinds.${scope.kind}`),
          },
          {
            key: 'roles',
            label: t('gov.admin.me.roles'),
            sortValue: (scope) => scope.roles.join(', '),
            cell: (scope) => (
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {scope.roles.map((role) => (
                  <TagChip key={role} label={t(`gov.admin.roles.${role}`)} />
                ))}
              </Stack>
            ),
          },
        ]}
      />

      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {/* `hereYouCan` names the place, so it is only usable once there is a
              place to name — otherwise it renders as "At  you can". */}
          {active
            ? t('gov.admin.me.hereYouCan', { scope: active.name })
            : t('gov.admin.me.anywhereYouCan')}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {notable.filter((p) => can(p)).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {active ? t('gov.admin.me.nothingHere') : t('gov.admin.me.nothingAnywhere')}
            </Typography>
          ) : (
            notable
              .filter((p) => can(p))
              .map((p) => <TagChip key={p} label={t(`gov.admin.permissions.${p}`)} />)
          )}
        </Stack>
      </HairlineCard>
    </Stack>
  );
}
